import React, { useState, useEffect } from 'react';
import { FiChevronUp, FiDroplet, FiEye, FiEyeOff } from 'react-icons/fi';
import { OUTPUT_COLOR_PALETTE, hasStylingOverride } from '../utils/chartDataDerivation';
import { getStylingControls, isNodeShownOnChart, resolveNodeOverlay, buildNodeVisualTogglePatch } from '../config/nodeVisualConfig';
import { useNodeVisualOverrides } from '../../../../../hooks/useVisualOverrides';
import { resolveMarkerStates } from '../utils/markerDefaults';
import { TRADE_MARKER_DEFAULTS, EXIT_OUTCOME_COLORS, isExitAction } from '../../../../../utils/tradeMarkerBuilder';
import { Tooltip } from '../../../../../components/ds';

// --- Constants ---

const CANDLE_THEMES = {
    STANDARD: {
        label: 'STD',
        up: '#00C292',
        down: '#E46A76',
        wickUp: '#00C292',
        wickDown: '#E46A76'
    },
    HIGH_CONTRAST: {
        label: 'H/C',
        up: '#2962FF',
        down: '#FF9800',
        wickUp: '#2962FF',
        wickDown: '#FF9800'
    },
    BLACK_WHITE: {
        label: 'B&W',
        up: '#E5E7EB',
        down: '#374151',
        wickUp: '#374151',
        wickDown: '#374151',
        borderUp: '#374151',
        borderDown: '#374151'
    },
    CUSTOM: {
        label: 'CUSTOM'
    }
};

const INDICATOR_THEMES = {
    'MACD': [
        {
            id: 'classic',
            label: 'Classic',
            styles: {
                macd: { color: '#2962FF', lineWidth: 2 },
                signal: { color: '#FF9800', lineWidth: 2, lineStyle: 0 },
                hist: { color: '#26a69a', colorPosDown: '#b2dfdb', colorPosUp: '#26a69a', colorNegDown: '#ef5350', colorNegUp: '#ffcdd2' }
            }
        },
        {
            id: 'high_contrast',
            label: 'Hi-Contrast',
            styles: {
                macd: { color: '#2962FF', lineWidth: 3 },
                signal: { color: '#FF5722', lineWidth: 3 },
                hist: { color: '#00C292', colorPosUp: '#00C292', colorPosDown: '#80E2C9', colorNegDown: '#E46A76', colorNegUp: '#F2B5BC' }
            }
        },
        {
            id: 'neon',
            label: 'Neon',
            styles: {
                macd: { color: '#00E5FF', lineWidth: 2 },
                signal: { color: '#F50057', lineWidth: 2 },
                hist: { color: '#00E5FF', colorPosUp: '#00E5FF', colorPosDown: '#80F2FF', colorNegDown: '#F50057', colorNegUp: '#FF80AB' }
            }
        }
    ],
    'RSI': [
        {
            id: 'classic',
            label: 'Classic',
            styles: { value: { color: '#B417EB', lineWidth: 2 } }
        },
        {
            id: 'green',
            label: 'Emerald',
            styles: { value: { color: '#00C292', lineWidth: 2 } }
        },
        {
            id: 'gold',
            label: 'Gold',
            styles: { value: { color: '#FF9800', lineWidth: 2 } }
        }
    ]
};

// --- Helper Components ---

const getEffectiveColor = (nodeData, defaultHex) => {
    return nodeData.color || nodeData.visual_settings?.color || defaultHex;
};

// Helper to ensure we have a valid hex for transparency operations
const ensureHexColor = (color) => {
    if (!color) return OUTPUT_COLOR_PALETTE[0];
    if (color.startsWith('#')) return color.substring(0, 7);
    
    // Create a temporary element to resolve color
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = color;
    return ctx.fillStyle; // Returns #RRGGBB
};

const ColorPicker = ({ label, color, onChange, opacity, hideLabel = false, small = false }) => {
    const PRESETS = OUTPUT_COLOR_PALETTE; // 142-49 slice 4: the one palette
    const baseColor = color?.length > 7 ? color.substring(0, 7) : (color || OUTPUT_COLOR_PALETTE[0]);
    
    const handleColorSelect = (newColor) => {
        let finalColor = newColor;
        if (opacity) finalColor = `${newColor.substring(0, 7)}${opacity}`;
        onChange(finalColor);
    };

    const sizeClass = small ? 'w-5 h-5' : 'w-6 h-6';

    return (
        <div className={hideLabel ? "" : "mb-3"}>
            {!hideLabel && <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-2">{label}</label>}
            <div className="flex gap-1.5 flex-wrap">
                {PRESETS.map(preset => (
                    <button
                        key={preset}
                        onClick={() => handleColorSelect(preset)}
                        className={`${sizeClass} rounded-full border-2 transition-all hover:scale-110 ${
                            baseColor.toLowerCase() === preset.toLowerCase()
                                ? 'border-accent ring-2 ring-offset-1 ring-[var(--divider)]' 
                                : 'border-transparent'
                        }`}
                        style={{ backgroundColor: preset }}
                    />
                ))}
                <label className="relative cursor-pointer group">
                    <input type="color" value={baseColor} onChange={(e) => handleColorSelect(e.target.value)} className="sr-only" />
                    <div className={`${sizeClass} rounded-full bg-gradient-to-tr from-bg-3 to-[var(--divider)] border border-dashed border-[var(--divider)] flex items-center justify-center hover:scale-110 transition-transform`}>
                        <FiDroplet size={small ? 8 : 10} className="text-fg-2" />
                    </div>
                </label>
            </div>
        </div>
    );
};

const OutputStylePicker = ({ label, style, onChange, isHistogram = false, isArea = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    const PRESETS = OUTPUT_COLOR_PALETTE; // 142-49 slice 4: the one palette
    const currentColor = style.color || OUTPUT_COLOR_PALETTE[0];
    const baseColor = currentColor.length > 7 ? currentColor.substring(0, 7) : currentColor;

    // Visual Preview Helper
    const renderPreview = () => {
        if (isHistogram) {
            return (
                <div className="flex gap-0.5 items-end h-3">
                    <div className="w-1 h-3 rounded-t-[1px]" style={{ backgroundColor: style.colorPosUp || '#26a69a' }} />
                    <div className="w-1 h-2 rounded-t-[1px]" style={{ backgroundColor: style.colorPosDown || '#b2dfdb' }} />
                    <div className="w-1 h-2 rounded-b-[1px] mb-[-4px]" style={{ backgroundColor: style.colorNegUp || '#ffcdd2' }} />
                    <div className="w-1 h-3 rounded-b-[1px] mb-[-4px]" style={{ backgroundColor: style.colorNegDown || '#ef5350' }} />
                </div>
            );
        }
        // Line Preview
        const dashArray = style.lineStyle === 1 ? '2,2' : style.lineStyle === 2 ? '4,4' : '';
        return (
            <div className="w-8 h-3 flex items-center justify-center">
                <div 
                    style={{ 
                        height: Math.max(1, (style.lineWidth || 2) / 2), 
                        width: '100%', 
                        backgroundColor: currentColor,
                        borderTop: style.lineStyle ? `1px dashed ${currentColor}` : undefined,
                        borderStyle: style.lineStyle === 1 ? 'dotted' : style.lineStyle === 2 ? 'dashed' : 'solid'
                    }} 
                />
            </div>
        );
    };

    return (
        <div className="bg-bg-3/50 dark:bg-bg-1/30 rounded-lg p-2 border border-divider/50 select-none">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                        {renderPreview()}
                    </div>
                    <span className="text-[10px] font-bold text-fg-3 uppercase tracking-tight truncate">{label}</span>
                </div>
                <div className="text-[9px] font-bold text-accent hover:text-accent uppercase flex items-center gap-1 flex-shrink-0 focus:outline-none">
                    {isExpanded ? 'Hide' : 'Edit'}
                    <FiChevronUp size={12} className={`transition-transform ${isExpanded ? '' : 'rotate-180'}`} />
                </div>
            </div>

            {isExpanded && (
                <div className="mt-3 space-y-4 pt-2 border-t border-[var(--divider)] cursor-auto" onClick={e => e.stopPropagation()}>

                    {/* 1. Color Presets (Using Standardized ColorPicker) */}
                    {!isHistogram && (
                        <div className="pt-1">
                             <ColorPicker 
                                hideLabel={true}
                                small={true}
                                color={currentColor}
                                onChange={(c) => onChange({ ...style, color: c })}
                             />
                        </div>
                    )}

                    {/* 2. Histogram Specific Controls (4-State) */}
                    {isHistogram && (
                        <div className="space-y-4 pt-2 border-t border-[var(--divider)] dark:border-white/5">
                             <div className="space-y-4">
                                <div className="space-y-3">
                                     <span className="text-[9px] font-bold text-fg-3 uppercase block border-b border-[var(--divider)] dark:border-white/5 pb-1">Positive (Above 0)</span>
                                     <div className="space-y-2">
                                         <span className="text-[9px] text-fg-3 uppercase font-bold">Growing</span>
                                         <ColorPicker 
                                            hideLabel={true} small={true}
                                            color={style.colorPosUp || '#26a69a'}
                                            onChange={(c) => onChange({ ...style, colorPosUp: c })}
                                         />
                                     </div>
                                     <div className="space-y-2">
                                         <span className="text-[9px] text-fg-3 uppercase font-bold">Shrinking</span>
                                         <ColorPicker 
                                            hideLabel={true} small={true}
                                            color={style.colorPosDown || '#b2dfdb'}
                                            onChange={(c) => onChange({ ...style, colorPosDown: c })}
                                         />
                                     </div>
                                </div>
                                <div className="space-y-3">
                                     <span className="text-[9px] font-bold text-fg-3 uppercase block border-b border-[var(--divider)] dark:border-white/5 pb-1">Negative (Below 0)</span>
                                     <div className="space-y-2">
                                         <span className="text-[9px] text-fg-3 uppercase font-bold">Growing</span>
                                         <ColorPicker 
                                            hideLabel={true} small={true}
                                            color={style.colorNegDown || '#ef5350'}
                                            onChange={(c) => onChange({ ...style, colorNegDown: c })}
                                         />
                                     </div>
                                     <div className="space-y-2">
                                         <span className="text-[9px] text-fg-3 uppercase font-bold">Shrinking</span>
                                         <ColorPicker 
                                            hideLabel={true} small={true}
                                            color={style.colorNegUp || '#ffcdd2'}
                                            onChange={(c) => onChange({ ...style, colorNegUp: c })}
                                         />
                                     </div>
                                </div>
                             </div>
                        </div>
                    )}

                    {/* 3. Line Controls (If NOT Histogram/Area) */}
                    {!isHistogram && !isArea && (
                        <div className="space-y-3 pt-2 border-t border-[var(--divider)] dark:border-white/5">
                            {/* Width */}
                            <div className="flex items-center justify-between">
                                <span className="text-[9px] text-fg-3 uppercase">Width</span>
                                <input
                                    type="range" min="1" max="4" value={style.lineWidth || 2}
                                    onChange={(e) => onChange({ ...style, lineWidth: parseInt(e.target.value) })}
                                    className="w-16 h-1 bg-bg-3 dark:bg-bg-3 rounded-full appearance-none cursor-pointer accent-cyan-500"
                                />
                            </div>

                            {/* Style (Solid/Dot/Dash) */}
                            <div className="flex bg-bg-3 dark:bg-bg-2 rounded p-0.5 gap-0.5">
                                {[{ l: 'Solid', v: 0 }, { l: 'Dot', v: 1 }, { l: 'Dash', v: 2 }].map(opt => (
                                    <button
                                        key={opt.l}
                                        onClick={() => onChange({ ...style, lineStyle: opt.v })}
                                        className={`flex-1 py-1 text-[8px] font-bold uppercase rounded ${
                                            (style.lineStyle || 0) === opt.v ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3'
                                        }`}
                                    >
                                        {opt.l}
                                    </button>
                                ))}
                            </div>
                            
                            {/* Smoothing (Simple/Step/Curved) */}
                             <div className="flex bg-bg-3 dark:bg-bg-2 rounded p-0.5 gap-0.5">
                                {[{ l: 'Simple', v: 0 }, { l: 'Step', v: 1 }, { l: 'Curved', v: 2 }].map(opt => (
                                    <button
                                        key={opt.l}
                                        onClick={() => onChange({ ...style, lineType: opt.v })}
                                        className={`flex-1 py-1 text-[8px] font-bold uppercase rounded ${
                                            (style.lineType || 0) === opt.v ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3'
                                        }`}
                                    >
                                        {opt.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- Control Components ---

const CandleThemeControl = ({ nodeData, updateNodeData }) => {
    const [activeTheme, setActiveTheme] = useState('STANDARD');

    useEffect(() => {
        const { upColor, downColor } = nodeData;
        if (!upColor && !downColor) {
            setActiveTheme('STANDARD');
            return;
        }

        for (const [key, theme] of Object.entries(CANDLE_THEMES)) {
            if (key === 'CUSTOM') continue;
            if (theme.up === upColor && theme.down === downColor) {
                setActiveTheme(key);
                return;
            }
        }
        setActiveTheme('CUSTOM');
    }, [nodeData.upColor, nodeData.downColor]);

    const handleThemeChange = (themeKey) => {
        setActiveTheme(themeKey);
        if (themeKey !== 'CUSTOM') {
            const theme = CANDLE_THEMES[themeKey];
            updateNodeData({ 
                upColor: theme.up, 
                downColor: theme.down,
                wickUpColor: theme.wickUp,
                wickDownColor: theme.wickDown,
                borderUpColor: theme.borderUp,
                borderDownColor: theme.borderDown,
                volumeOpacity: 50
            });
        }
    };

    return (
        <div className="space-y-4">
             <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-3">Theme</label>
             <div className="flex p-1 bg-bg-3 dark:bg-bg-1 rounded-lg">
                {Object.entries(CANDLE_THEMES).map(([key, theme]) => (
                    <button
                        key={key}
                        onClick={() => handleThemeChange(key)}
                        className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${
                            activeTheme === key ? 'bg-white dark:bg-bg-3 text-fg-1 dark:text-white shadow-sm' : 'text-fg-3 hover:text-fg-1'
                        }`}
                    >
                        {theme.label}
                    </button>
                ))}
            </div>
            {activeTheme === 'CUSTOM' && (
                <div className="grid grid-cols-1 gap-4 pt-2">
                     <div className="space-y-2">
                        <span className="text-[10px] font-bold text-fg-3 uppercase border-b border-[var(--divider)] dark:border-white/5 pb-1 block">Bullish</span>
                        <ColorPicker 
                            hideLabel={true}
                            color={nodeData.upColor || '#00C292'} 
                            onChange={(c) => updateNodeData({ upColor: c })} 
                        />
                     </div>
                     <div className="space-y-2">
                        <span className="text-[10px] font-bold text-fg-3 uppercase border-b border-[var(--divider)] dark:border-white/5 pb-1 block">Bearish</span>
                        <ColorPicker 
                            hideLabel={true}
                            color={nodeData.downColor || '#E46A76'} 
                            onChange={(c) => updateNodeData({ downColor: c })} 
                        />
                     </div>
                </div>
            )}
        </div>
    );
};

//* 142-49 slice 4: the single-colour pickers default to the FIRST output's colour from the
//* one authority (defaultOutputStyles), never a literal of their own
const firstOutputColor = (dos) => Object.values(dos || {})[0]?.color || OUTPUT_COLOR_PALETTE[0];

const LineColorControl = ({ nodeData, updateNodeData, defaultOutputStyles }) => (
    <ColorPicker 
        label="Line Color" 
        color={getEffectiveColor(nodeData, firstOutputColor(defaultOutputStyles))} 
        onChange={(c) => updateNodeData({ color: c })} 
    />
);

const AreaColorsControl = ({ nodeData, updateNodeData, defaultOutputStyles }) => (
    <div className="grid grid-cols-1 gap-1">
        <ColorPicker 
            label="Line Color" 
            color={nodeData.lineColor || getEffectiveColor(nodeData, firstOutputColor(defaultOutputStyles))} 
            onChange={(c) => updateNodeData({ lineColor: c, color: c })} 
        />
        <ColorPicker 
            label="Fill Color" 
            color={nodeData.topColor || '#2962FF40'} 
            opacity="40" 
            onChange={(c) => updateNodeData({ topColor: c })} 
        />
    </div>
);

const BandColorsControl = ({ nodeData, updateNodeData }) => (
    <div className="space-y-1">
        <ColorPicker 
            label="Border Color" 
            color={nodeData.lineColor || getEffectiveColor(nodeData, '#607D8B')} 
            onChange={(c) => updateNodeData({ lineColor: c, color: c })} 
        />
        <ColorPicker 
            label="Fill Color" 
            color={nodeData.fillColor || '#607D8B20'} 
            opacity="20"
            onChange={(c) => updateNodeData({ fillColor: c })} 
        />
    </div>
);

const OpacityControl = ({ nodeData, updateNodeData }) => (
    <div className="pt-4 border-t border-[var(--divider)] dark:border-white/5 mt-2">
        <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-fg-3 uppercase tracking-widest">Transparency</span>
            <span className="text-xs font-bold text-fg-1">{nodeData.volumeOpacity ?? 50}%</span>
        </div>
        <input
            type="range" min="0" max="100" value={nodeData.volumeOpacity ?? 50}
            onChange={(e) => updateNodeData({ volumeOpacity: parseInt(e.target.value) })}
            className="w-full h-1.5 bg-bg-3 dark:bg-bg-3 rounded-full appearance-none cursor-pointer accent-cyan-500"
        />
    </div>
);

const LineThicknessControl = ({ nodeData, updateNodeData }) => (
    <div>
        <div className="flex items-center justify-between mb-2 mt-4">
            <span className="text-sm font-medium text-fg-1">Line Thickness</span>
            <span className="text-sm font-bold text-fg-1">{nodeData.lineWidth || 2}px</span>
        </div>
        <input
            type="range" min="1" max="5" value={nodeData.lineWidth || 2}
            onChange={(e) => updateNodeData({ lineWidth: parseInt(e.target.value) })}
            className="w-full h-2 bg-bg-3 dark:bg-bg-3 rounded-lg appearance-none cursor-pointer accent-cyan-500"
        />
    </div>
);

const LineStyleControl = ({ nodeData, updateNodeData }) => (
    <div className="mt-4">
        <label className="block text-xs font-medium text-fg-1 mb-2">Style</label>
        <div className="flex bg-bg-3 dark:bg-bg-1 rounded-lg p-1 gap-1">
            {[{ label: 'Solid', val: 0 }, { label: 'Dotted', val: 1 }, { label: 'Dashed', val: 2 }].map(opt => (
                <button
                    key={opt.label}
                    onClick={() => updateNodeData({ lineStyle: opt.val })}
                    className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded transition-all ${
                        (nodeData.lineStyle || 0) === opt.val ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3 hover:text-fg-2'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    </div>
);

//* 50-7 — Pane placement: price overlay vs its own sub-pane. Writes the node's
//* `visual_settings.is_overlay`, which the chart derivation honours over the type default
//* (math → sub-pane) and over backend indicator metadata.
const PaneControl = ({ nodeData, updateNodeData, nodeType }) => {
    const isOverlay = resolveNodeOverlay(nodeType, nodeData);
    return (
        <div>
            <label className="block text-xs font-medium text-fg-1 mb-2">Pane</label>
            <div className="flex bg-bg-3 dark:bg-bg-1 rounded-lg p-1 gap-1" role="radiogroup" aria-label="Pane">
                {[{ label: 'Price overlay', val: true }, { label: 'Sub-pane', val: false }].map(opt => (
                    <button
                        key={opt.label}
                        role="radio"
                        aria-checked={isOverlay === opt.val}
                        onClick={() => updateNodeData(buildNodeVisualTogglePatch(nodeType, nodeData, 'overlay', { value: opt.val }))}
                        className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded transition-all ${
                            isOverlay === opt.val ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3 hover:text-fg-2'
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const LineTypeControl = ({ nodeData, updateNodeData }) => (
    <div className="mt-4">
        <label className="block text-xs font-medium text-fg-1 mb-2">Smoothing</label>
        <div className="flex bg-bg-3 dark:bg-bg-1 rounded-lg p-1 gap-1">
            {[{ label: 'Simple', val: 0 }, { label: 'Step', val: 1 }, { label: 'Curved', val: 2 }].map(opt => (
                <button
                    key={opt.label}
                    onClick={() => updateNodeData({ lineType: opt.val })}
                    className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded transition-all ${
                        (nodeData.lineType || 0) === opt.val ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3 hover:text-fg-2'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    </div>
);

const IndicatorThemeControl = ({ nodeData, updateNodeData }) => {
    const indicatorType = nodeData.indicator?.toUpperCase();
    const themes = INDICATOR_THEMES[indicatorType];

    if (!themes) return null;

    const applyTheme = (theme) => {
        updateNodeData({
            outputStyles: {
                ...(nodeData.outputStyles || {}),
                ...theme.styles
            }
        });
    };

    return (
        <div className="space-y-3 mb-6">
            <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-3">Predefined Palette</label>
            <div className="flex flex-wrap gap-2">
                {themes.map(theme => (
                    <button
                        key={theme.id}
                        onClick={() => applyTheme(theme)}
                        className="px-3 py-1.5 rounded-lg bg-bg-3 dark:bg-bg-1 border border-[var(--divider)] text-[10px] font-bold text-fg-3 hover:border-accent/50 hover:text-accent transition-all uppercase tracking-tight shadow-sm"
                    >
                        {theme.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const ChartTypeControl = ({ nodeData, updateNodeData, nodeType, defaultOutputStyles }) => {
    // Determine allowed types
    const baseType = nodeData.visual_settings?.type?.toLowerCase() || 'line';
    let allowedTypes = ['line'];

    if (nodeType === 'math') allowedTypes = ['line'];
    else if (nodeType === 'comparator' || nodeType === 'logic_gate' || baseType === 'marker') allowedTypes = ['marker'];
    else if (['area', 'band'].includes(baseType)) allowedTypes = ['line', 'area'];
    else if (baseType === 'histogram') allowedTypes = ['line', 'histogram', 'area'];
    else if (baseType === 'band') allowedTypes = ['line', 'band', 'area'];
    else if (['dual_area', 'multi_area'].includes(baseType)) allowedTypes = ['line', 'area', 'dual_area'];
    
    // Ensure current is available
    if (nodeData.chartType && !allowedTypes.includes(nodeData.chartType)) allowedTypes.push(nodeData.chartType);

    return (
        <div className="mb-6">
             <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-3">Plot Style</label>
             <div className="flex flex-wrap gap-1 p-1 bg-bg-3 dark:bg-bg-1 rounded-lg">
                 {allowedTypes.map(type => (
                     <button
                        key={type}
                        onClick={() => {
                            const updates = { chartType: type };
                            // Apply defaults for Area if missing
                            if (type === 'area' && !nodeData.topColor) {
                                const rawColor = getEffectiveColor(nodeData, firstOutputColor(defaultOutputStyles));
                                const base = ensureHexColor(rawColor);
                                updates.topColor = `${base}40`; // 25% opacity
                                updates.bottomColor = `${base}00`; // 0% opacity
                                updates.lineColor = rawColor;
                            }
                            updateNodeData(updates);
                        }}
                        className={`flex-1 min-w-[60px] py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${
                            (nodeData.chartType || 'line') === type ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3 hover:text-fg-1'
                        }`}
                     >
                         {type}
                     </button>
                 ))}
             </div>
        </div>
    );
};

// --- New Helper Component for Marker Styling ---
const MarkerStyleSection = ({ label, settings, onUpdate, onToggleHide, isRemovable = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { shape, color } = settings;

    // Preview Render
    const renderPreview = () => {
        const char = shape === 'arrowUp' ? '↑' : shape === 'arrowDown' ? '↓' : shape === 'circle' ? '●' : '■';
        return (
            <span className="inline-flex w-4 h-4 items-center justify-center rounded bg-bg-3 dark:bg-bg-2 text-[10px] font-bold shadow-sm border border-[var(--divider)]" style={{ color: color || '#2962FF' }}>
                {char}
            </span>
        );
    };

    return (
        <div className="bg-bg-3/50 dark:bg-bg-1/30 rounded-lg p-2 border border-divider/50 select-none">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                        {renderPreview()}
                    </div>
                    <span className="text-[10px] font-bold text-fg-3 uppercase tracking-tight truncate">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                    {isRemovable && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onToggleHide(); }}
                            className="text-[9px] font-bold text-fg-3 hover:text-loss uppercase px-1.5 py-0.5 rounded transition-colors focus:outline-none"
                        >
                            Hide
                        </button>
                    )}
                    <div className="text-[9px] font-bold text-accent hover:text-accent uppercase flex items-center gap-1 flex-shrink-0 focus:outline-none">
                        {isExpanded ? 'Hide' : 'Edit'}
                        <FiChevronUp size={12} className={`transition-transform ${isExpanded ? '' : 'rotate-180'}`} />
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="mt-3 space-y-3 pt-2 border-t border-[var(--divider)] cursor-auto" onClick={e => e.stopPropagation()}>
                     <div className="flex items-center justify-between">
                         <span className="text-[9px] font-bold text-fg-3 uppercase">Shape</span>
                         <div className="flex bg-bg-3 dark:bg-bg-2 rounded p-0.5 gap-0.5">
                            {['arrowUp', 'arrowDown', 'circle', 'square'].map(s => (
                                <button 
                                    key={s} 
                                    onClick={() => onUpdate({ shape: s })}
                                    className={`w-6 h-6 flex items-center justify-center rounded text-[10px] transition-all ${
                                        (shape || 'circle') === s 
                                            ? 'bg-white dark:bg-bg-3 text-accent shadow-sm ring-1 ring-black/5 dark:ring-white/10' 
                                            : 'text-fg-3 hover:text-fg-2'
                                    }`}
                                >
                                    {s==='arrowUp'?'↑':s==='arrowDown'?'↓':s==='circle'?'●':'■'}
                                </button>
                            ))}
                         </div>
                     </div>
                     <div className="space-y-2">
                         <span className="text-[9px] font-bold text-fg-3 uppercase">Color</span>
                         <ColorPicker 
                            hideLabel={true}
                            small={true}
                            color={color || '#2962FF'}
                            onChange={(c) => onUpdate({ color: c })}
                         />
                     </div>
                </div>
            )}
        </div>
    );
};

const MarkerSettingsControl = ({ nodeData, updateNodeData }) => {
    const states = resolveMarkerStates(nodeData.visual_settings, nodeData.styleOptions);
    const markerSettings = nodeData.styleOptions?.markerSettings || {};
    const isSignalMarkersEnabled = markerSettings.enabled !== false;
    return (
    <div className="space-y-4 pt-2">
         {/* Header + Master Toggle */}
         <div className="flex items-center justify-between">
            <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest">Signal Markers</label>
            <div className="flex items-center gap-2">
                 <button
                    onClick={() => {
                        const currentSettings = nodeData.styleOptions?.markerSettings || {};
                        const currentShow = currentSettings.showText ?? true;
                        updateNodeData({
                            styleOptions: {
                                ...(nodeData.styleOptions || {}),
                                markerSettings: { ...currentSettings, showText: !currentShow }
                            }
                        });
                    }}
                    className={`flex items-center gap-1 text-[9px] font-bold uppercase px-2 py-1 rounded transition-colors ${
                        (markerSettings.showText ?? true) ? 'bg-accent/10/10 text-accent' : 'bg-bg-3 dark:bg-bg-2 text-fg-3'
                    }`}
                >
                    {(markerSettings.showText ?? true) ? <FiEye size={10}/> : <FiEyeOff size={10}/>}
                    Labels
                </button>
                <button
                    onClick={() => updateNodeData({
                        styleOptions: {
                            ...(nodeData.styleOptions || {}),
                            markerSettings: { ...markerSettings, enabled: !isSignalMarkersEnabled }
                        }
                    })}
                    className={`text-[9px] font-bold uppercase px-2 py-1 rounded transition-colors ${
                        isSignalMarkersEnabled ? 'bg-profit/10 text-profit' : 'bg-bg-3 dark:bg-bg-2 text-fg-3'
                    }`}
                >
                    {isSignalMarkersEnabled ? 'On' : 'Off'}
                </button>
            </div>
        </div>

        {!isSignalMarkersEnabled ? null : (<>

        {/* Size Slider */}
        <div className="flex items-center justify-between gap-4 py-1 border-b border-divider/50 pb-3">
             <span className="text-[10px] font-bold text-fg-3 uppercase tracking-tight">Global Size</span>
             <input 
                type="range" min="0.5" max="4" step="0.1" 
                value={nodeData.styleOptions?.markerSettings?.size ?? 2}
                onChange={(e) => {
                    const current = nodeData.styleOptions?.markerSettings || {};
                    updateNodeData({ styleOptions: { ...(nodeData.styleOptions || {}), markerSettings: { ...current, size: parseFloat(e.target.value) } } });
                }}
                className="flex-1 accent-cyan-500 h-1 appearance-none bg-bg-3 dark:bg-bg-3 rounded-full"
            />
        </div>

        {/* State Sections */}
        {states.map(stateDef => {
             const keyPrefix = stateDef.key;
             const settings = nodeData.styleOptions?.markerSettings || {};
             
             // Backward compatibility trick for "false" state
             const showStateKey = `show_${keyPrefix}`;
             const isBackwardCompatFalse = keyPrefix === 'false' && settings.showFalseMarkers !== undefined;
             const isActive = isBackwardCompatFalse ? settings.showFalseMarkers : (settings[showStateKey] !== undefined ? settings[showStateKey] : (stateDef.enabled !== false));
             
             const defaultColor = stateDef.color || '#10B981';
             const defaultShape = stateDef.shape || 'arrowUp';

             if (!isActive) return (
                 <div key={keyPrefix} className="flex justify-between py-2 px-2 border border-dashed border-[var(--divider)] rounded-lg opacity-60 hover:opacity-100 hover:border-accent/30 dark:hover:border-accent transition-all cursor-pointer"
                    onClick={() => updateNodeData({ styleOptions: { ...(nodeData.styleOptions || {}), markerSettings: { ...settings, [showStateKey]: true, ...(keyPrefix === 'false' ? {showFalseMarkers: true} : {}) } } })}
                 >
                     <span className="text-[10px] font-bold text-fg-3 uppercase">{stateDef.label}</span>
                     <span className="text-[9px] font-bold text-accent bg-accent/10/10 px-2 py-0.5 rounded border border-accent/20/50">+ Enable</span>
                 </div>
             );

             return (
                 <MarkerStyleSection 
                    key={keyPrefix}
                    label={`${stateDef.label}`}
                    settings={{
                        shape: settings[`${keyPrefix}Shape`] || defaultShape,
                        color: settings[`${keyPrefix}Color`] || defaultColor
                    }}
                    onUpdate={(updates) => updateNodeData({ 
                        styleOptions: { 
                            ...(nodeData.styleOptions || {}), 
                            markerSettings: { ...settings, [`${keyPrefix}Shape`]: updates.shape || settings[`${keyPrefix}Shape`] || defaultShape, [`${keyPrefix}Color`]: updates.color || settings[`${keyPrefix}Color`] || defaultColor } 
                        } 
                    })}
                    isRemovable={true}
                    onToggleHide={() => updateNodeData({ styleOptions: { ...(nodeData.styleOptions || {}), markerSettings: { ...settings, [showStateKey]: false, ...(keyPrefix === 'false' ? {showFalseMarkers: false} : {}) } } })}
                 />
             );
        })}
    </>)}
    </div>
    );
};

// --> Trade Marker Settings: configures how trade execution markers appear on chart
const POSITION_OPTIONS = [
    { value: 'belowBar', label: 'Below Bar' },
    { value: 'aboveBar', label: 'Above Bar' },
    { value: 'inBar', label: 'In Bar' },
];

const LABEL_MODE_OPTIONS = [
    { value: 'action_price', label: 'Action + Price' },
    { value: 'price', label: 'Price Only' },
    { value: 'off', label: 'Off' },
];

const SHAPE_OPTIONS = [
    { value: 'arrowUp', icon: '↑' },
    { value: 'arrowDown', icon: '↓' },
    { value: 'circle', icon: '●' },
    { value: 'square', icon: '■' },
];

//--> Level-display (SL/TP/exit line) action set -- independent of the marker-colour exit set, which
//*   comes from the exit-reason registry (isExitAction).
const LEVEL_DISPLAY_EXIT_ACTIONS = ['sl', 'tp', 'exit_long', 'exit_short', 'time_exit'];

const TradeMarkerSettingsControl = ({ nodeData, updateNodeData }) => {
    //--> Merge per-action defaults under persisted settings so brand-new nodes
    //    (no visual_settings.trade_markers yet) show correct default values in
    //    the UI controls. Same defaults table as tradeMarkerBuilder uses at render.
    const actionDefaults = TRADE_MARKER_DEFAULTS[nodeData.action] || TRADE_MARKER_DEFAULTS.enter_long;
    const persisted = nodeData.visual_settings?.trade_markers || {};
    const tradeMarkers = {
        ...actionDefaults,
        ...persisted,
        label: { ...actionDefaults.label, ...(persisted.label || {}) },
    };
    const isEnabled = tradeMarkers.enabled !== false;
    const isExitNode = isExitAction(nodeData.action);

    //--> Determine if this is a shared/global exit with multi-stage
    const stageOverrides = tradeMarkers.stageOverrides || {};
    const stageKeys = Object.keys(stageOverrides).map(Number).sort((a, b) => a - b);
    const hasStages = stageKeys.length > 0;
    const [activeStageTab, setActiveStageTab] = React.useState('all');

    //--> Get the settings for the active tab (base or stage override)
    const getActiveSettings = () => {
        if (activeStageTab === 'all') return tradeMarkers;
        const override = stageOverrides[activeStageTab] || {};
        return { ...tradeMarkers, ...override, label: { ...(tradeMarkers.label || {}), ...(override.label || {}) } };
    };

    const activeSettings = getActiveSettings();

    //--> Update helper: writes to base or stageOverrides depending on active tab
    const updateTradeMarkers = (updates) => {
        if (activeStageTab === 'all') {
            updateNodeData({
                visual_settings: {
                    ...(nodeData.visual_settings || {}),
                    trade_markers: { ...tradeMarkers, ...updates }
                }
            });
        } else {
            const currentOverride = stageOverrides[activeStageTab] || {};
            updateNodeData({
                visual_settings: {
                    ...(nodeData.visual_settings || {}),
                    trade_markers: {
                        ...tradeMarkers,
                        stageOverrides: {
                            ...stageOverrides,
                            [activeStageTab]: { ...currentOverride, ...updates }
                        }
                    }
                }
            });
        }
    };

    const clearStageOverride = () => {
        const { [activeStageTab]: _, ...rest } = stageOverrides;
        updateNodeData({
            visual_settings: {
                ...(nodeData.visual_settings || {}),
                trade_markers: { ...tradeMarkers, stageOverrides: rest }
            }
        });
        setActiveStageTab('all');
    };

    return (
        <div className="space-y-4 pt-2">
            {/* Header + Master Toggle */}
            <div className="flex items-center justify-between">
                <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest">Trade Markers</label>
                <button
                    onClick={() => updateNodeData({
                        visual_settings: {
                            ...(nodeData.visual_settings || {}),
                            trade_markers: { ...tradeMarkers, enabled: !isEnabled }
                        }
                    })}
                    className={`text-[9px] font-bold uppercase px-2 py-1 rounded transition-colors ${
                        isEnabled ? 'bg-profit/10 text-profit' : 'bg-bg-3 dark:bg-bg-2 text-fg-3'
                    }`}
                >
                    {isEnabled ? 'On' : 'Off'}
                </button>
            </div>

            {isEnabled && (
                <>
                    {/* Stage Tabs (only for shared/global exits with overrides) */}
                    {hasStages && (
                        <div className="flex gap-1 bg-bg-3 dark:bg-bg-2 rounded p-0.5">
                            <button
                                onClick={() => setActiveStageTab('all')}
                                className={`text-[9px] font-bold px-2 py-1 rounded transition-all ${
                                    activeStageTab === 'all' ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3 hover:text-fg-2'
                                }`}
                            >All</button>
                            {stageKeys.map(s => (
                                <button key={s}
                                    onClick={() => setActiveStageTab(s)}
                                    className={`text-[9px] font-bold px-2 py-1 rounded transition-all ${
                                        activeStageTab === s ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3 hover:text-fg-2'
                                    }`}
                                >S{s}</button>
                            ))}
                        </div>
                    )}

                    {/* Reset to default (only on stage tabs) */}
                    {activeStageTab !== 'all' && (
                        <button onClick={clearStageOverride}
                            className="text-[9px] text-loss hover:text-loss font-bold uppercase"
                        >Reset to default</button>
                    )}

                    {/* Shape Selector */}
                    <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold text-fg-3 uppercase">Shape</span>
                        <div className="flex bg-bg-3 dark:bg-bg-2 rounded p-0.5 gap-0.5">
                            {SHAPE_OPTIONS.map(s => (
                                <button key={s.value}
                                    onClick={() => updateTradeMarkers({ shape: s.value })}
                                    className={`w-6 h-6 flex items-center justify-center rounded text-[10px] transition-all ${
                                        (activeSettings.shape || 'arrowUp') === s.value
                                            ? 'bg-white dark:bg-bg-3 text-accent shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                                            : 'text-fg-3 hover:text-fg-2'
                                    }`}
                                >{s.icon}</button>
                            ))}
                        </div>
                    </div>

                    {/* Color: exit markers are outcome-coloured (fixed legend); entry markers stay editable */}
                    {isExitNode ? (
                        <div className="flex items-center justify-between" data-testid="exit-outcome-legend">
                            <span className="text-[9px] font-bold text-fg-3 uppercase">Color</span>
                            <div className="flex items-center gap-2 text-[9px] font-bold text-fg-3">
                                {[['profit', 'Profit'], ['loss', 'Loss'], ['neutral', 'Neutral']].map(([k, label]) => (
                                    <span key={k} className="flex items-center gap-1">
                                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: EXIT_OUTCOME_COLORS[k] }} />
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <span className="text-[9px] font-bold text-fg-3 uppercase">Color</span>
                            <ColorPicker
                                hideLabel={true}
                                small={true}
                                color={activeSettings.color || '#10B981'}
                                onChange={(c) => updateTradeMarkers({ color: c })}
                            />
                        </div>
                    )}

                    {/* Position Selector */}
                    <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold text-fg-3 uppercase">Position</span>
                        <select
                            value={activeSettings.position || 'belowBar'}
                            onChange={(e) => updateTradeMarkers({ position: e.target.value })}
                            className="text-[10px] font-bold bg-bg-3 dark:bg-bg-2 text-fg-1 rounded px-2 py-1 border-none outline-none cursor-pointer"
                        >
                            {POSITION_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Size Slider */}
                    <div className="flex items-center justify-between gap-4 py-1 border-b border-divider/50 pb-3">
                        <span className="text-[10px] font-bold text-fg-3 uppercase tracking-tight">Size</span>
                        <input
                            type="range" min="0.5" max="4" step="0.1"
                            value={activeSettings.size ?? 2}
                            onChange={(e) => updateTradeMarkers({ size: parseFloat(e.target.value) })}
                            className="flex-1 accent-cyan-500 h-1 appearance-none bg-bg-3 dark:bg-bg-3 rounded-full"
                        />
                        <span className="text-[9px] font-mono text-fg-3 w-6 text-right">{(activeSettings.size ?? 2).toFixed(1)}</span>
                    </div>

                    {/* Auto-flip Toggle (exit nodes only) */}
                    {isExitNode && (
                        <div className="flex items-center justify-between py-1">
                            <span className="text-[9px] font-bold text-fg-3 uppercase">Auto-flip for exits</span>
                            <button
                                onClick={() => updateTradeMarkers({ autoFlipPosition: !activeSettings.autoFlipPosition })}
                                className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
                                    activeSettings.autoFlipPosition ? 'bg-accent' : 'bg-bg-3 dark:bg-bg-3'
                                }`}
                            >
                                <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                                    activeSettings.autoFlipPosition ? 'translate-x-[18px]' : 'translate-x-0.5'
                                }`} />
                            </button>
                        </div>
                    )}

                    {/* Label Mode */}
                    <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold text-fg-3 uppercase">Label</span>
                        <select
                            value={activeSettings.label?.mode || 'action_price'}
                            onChange={(e) => updateTradeMarkers({ label: { ...(activeSettings.label || {}), enabled: e.target.value !== 'off', mode: e.target.value } })}
                            className="text-[10px] font-bold bg-bg-3 dark:bg-bg-2 text-fg-1 rounded px-2 py-1 border-none outline-none cursor-pointer"
                        >
                            {LABEL_MODE_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </>
            )}
        </div>
    );
};

// --> Level Display Settings (exit nodes: SL/TP/exit line visualization)
const LEVEL_MODE_OPTIONS = [
    { value: 'price_lines', label: 'Price Lines' },
    { value: 'scoped_lines', label: 'Scoped Lines' },
    { value: 'band', label: 'Band Fill' },
];

const LEVEL_GLOBAL_KEYS = ['mode', 'hover_only', 'enabled', 'lines'];

// --> Per-line style picker (mirrors OutputStylePicker pattern)
const LevelLineStylePicker = ({ label, style, defaultColor, onChange }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const color = style.color || defaultColor;
    const lineStyle = style.line_style || 'dashed';
    const lineWidth = style.line_width || 2;

    const renderPreview = () => {
        const borderStyle = lineStyle === 'dotted' ? 'dotted' : lineStyle === 'dashed' ? 'dashed' : 'solid';
        return (
            <div className="w-8 h-3 flex items-center justify-center">
                <div style={{ height: Math.max(1, lineWidth / 2), width: '100%', backgroundColor: color, borderStyle }} />
            </div>
        );
    };

    return (
        <div className="bg-bg-3/50 dark:bg-bg-1/30 rounded-lg p-2 border border-divider/50 select-none">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">{renderPreview()}</div>
                    <span className="text-[10px] font-bold text-fg-3 uppercase tracking-tight truncate">{label}</span>
                </div>
                <div className="text-[9px] font-bold text-accent hover:text-accent uppercase flex items-center gap-1 flex-shrink-0">
                    {isExpanded ? 'Hide' : 'Edit'}
                    <FiChevronUp size={12} className={`transition-transform ${isExpanded ? '' : 'rotate-180'}`} />
                </div>
            </div>

            {isExpanded && (
                <div className="mt-3 space-y-3 pt-2 border-t border-[var(--divider)] cursor-auto" onClick={e => e.stopPropagation()}>
                    {/* Color */}
                    <div className="pt-1">
                        <ColorPicker hideLabel={true} small={true} color={color} onChange={(c) => onChange({ ...style, color: c })} />
                    </div>

                    {/* Width */}
                    <div className="flex items-center justify-between">
                        <span className="text-[9px] text-fg-3 uppercase">Width</span>
                        <input
                            type="range" min="1" max="4" value={lineWidth}
                            onChange={(e) => onChange({ ...style, line_width: parseInt(e.target.value) })}
                            className="w-16 h-1 bg-bg-3 dark:bg-bg-3 rounded-full appearance-none cursor-pointer accent-cyan-500"
                        />
                    </div>

                    {/* Style (Solid/Dashed/Dotted) */}
                    <div className="flex bg-bg-3 dark:bg-bg-2 rounded p-0.5 gap-0.5">
                        {[{ l: 'Solid', v: 'solid' }, { l: 'Dashed', v: 'dashed' }, { l: 'Dotted', v: 'dotted' }].map(opt => (
                            <button
                                key={opt.l}
                                onClick={() => onChange({ ...style, line_style: opt.v })}
                                className={`flex-1 py-1 text-[8px] font-bold uppercase rounded ${
                                    lineStyle === opt.v ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3'
                                }`}
                            >
                                {opt.l}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const LEVEL_LINE_DEFAULTS = {
    entry: { color: '#FFFFFF', line_width: 2, line_style: 'solid' },
    sl: { color: '#EF4444', line_width: 2, line_style: 'dashed' },
    tp: { color: '#10B981', line_width: 2, line_style: 'dashed' },
};

const LevelDisplayControl = ({ nodeData, updateNodeData, nodeId, allNodes = [], updateNodeDataById }) => {
    const isExitNode = LEVEL_DISPLAY_EXIT_ACTIONS.includes(nodeData.action);
    if (!isExitNode) return null;

    const levelDisplay = nodeData.visual_settings?.level_display || {};
    const isEnabled = levelDisplay.enabled !== false;
    const lines = levelDisplay.lines || {};

    //--> Sync global level display settings across ALL exit nodes
    const updateLevelDisplay = (updates) => {
        //--> Update the current node
        updateNodeData({
            visual_settings: {
                ...(nodeData.visual_settings || {}),
                level_display: { ...levelDisplay, ...updates }
            }
        });

        //--> Sync global keys to other exit nodes
        const hasGlobalKey = Object.keys(updates).some(k => LEVEL_GLOBAL_KEYS.includes(k));
        if (hasGlobalKey && updateNodeDataById && allNodes.length > 0) {
            const globalUpdates = {};
            for (const k of LEVEL_GLOBAL_KEYS) {
                if (k in updates) globalUpdates[k] = updates[k];
            }
            for (const n of allNodes) {
                if (n.id === nodeId) continue;
                if (!LEVEL_DISPLAY_EXIT_ACTIONS.includes(n.data?.action)) continue;
                const otherLd = n.data?.visual_settings?.level_display || {};
                //--> Deep-merge `lines` per key so each node's existing line styles are preserved
                const mergedUpdates = { ...globalUpdates };
                if (mergedUpdates.lines) {
                    mergedUpdates.lines = {
                        ...(otherLd.lines || {}),
                        ...mergedUpdates.lines,
                    };
                }
                updateNodeDataById(n.id, {
                    visual_settings: {
                        ...(n.data?.visual_settings || {}),
                        level_display: { ...otherLd, ...mergedUpdates }
                    }
                });
            }
        }
    };

    const updateLineStyle = (key, style) => {
        updateLevelDisplay({ lines: { ...lines, [key]: style } });
    };

    const activeMode = levelDisplay.mode || 'price_lines';

    return (
        <div className="space-y-3 pt-2 border-t border-divider/30">
            {/* Header + Master Toggle */}
            <div className="flex items-center justify-between">
                <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest">Level Display</label>
                <button
                    onClick={() => updateLevelDisplay({ enabled: !isEnabled })}
                    className={`text-[9px] font-bold uppercase px-2 py-1 rounded transition-colors ${
                        isEnabled ? 'bg-profit/10 text-profit' : 'bg-bg-3 dark:bg-bg-2 text-fg-3'
                    }`}
                >
                    {isEnabled ? 'On' : 'Off'}
                </button>
            </div>

            {isEnabled && (
                <>
                    {/* Mode Selector — Segmented Pill */}
                    <div>
                        <span className="text-[9px] font-bold text-fg-3 uppercase mb-1.5 block">Mode</span>
                        <div className="flex bg-bg-3 dark:bg-bg-1 rounded-lg p-1 gap-1">
                            {LEVEL_MODE_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => updateLevelDisplay({ mode: opt.value })}
                                    className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded transition-all ${
                                        activeMode === opt.value ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3 hover:text-fg-2'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Hover Only Toggle */}
                    <div className="flex items-center justify-between py-1">
                        <span className="text-[9px] font-bold text-fg-3 uppercase">Hover Only</span>
                        <button
                            onClick={() => updateLevelDisplay({ hover_only: !(levelDisplay.hover_only !== false) })}
                            className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
                                (levelDisplay.hover_only !== false) ? 'bg-accent' : 'bg-bg-3 dark:bg-bg-3'
                            }`}
                        >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                                (levelDisplay.hover_only !== false) ? 'translate-x-[18px]' : 'translate-x-0.5'
                            }`} />
                        </button>
                    </div>

                    {/* Per-Line Style Sections (OutputStylePicker pattern) */}
                    <div>
                        <span className="text-[9px] font-bold text-fg-3 uppercase mb-1.5 block">Line Styles</span>
                        <div className="space-y-2">
                            <LevelLineStylePicker
                                label="Entry"
                                defaultColor={LEVEL_LINE_DEFAULTS.entry.color}
                                style={lines.entry || LEVEL_LINE_DEFAULTS.entry}
                                onChange={(s) => updateLineStyle('entry', s)}
                            />
                            <LevelLineStylePicker
                                label="Stop Loss"
                                defaultColor={LEVEL_LINE_DEFAULTS.sl.color}
                                style={lines.sl || LEVEL_LINE_DEFAULTS.sl}
                                onChange={(s) => updateLineStyle('sl', s)}
                            />
                            <LevelLineStylePicker
                                label="Take Profit"
                                defaultColor={LEVEL_LINE_DEFAULTS.tp.color}
                                style={lines.tp || LEVEL_LINE_DEFAULTS.tp}
                                onChange={(s) => updateLineStyle('tp', s)}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const DeeTrendSlopeControl = ({ nodeData, updateNodeData, nodeId }) => {
    return (
        <div className="space-y-4">
            {/* ... Viz Mode & Compact Toggle ... */}
            <div>
                <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-3">Visualization Mode</label>
                <div className="flex p-1 bg-bg-3 dark:bg-bg-1 rounded-lg gap-1 mb-3">
                    {[{ label: 'Classic', val: 'classic' }, { label: 'Premium', val: 'premium' }].map(opt => (
                        <button
                            key={opt.val}
                            onClick={() => updateNodeData({ styleOptions: { ...(nodeData.styleOptions || {}), visualMode: opt.val } })}
                            className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded transition-all ${
                                (nodeData.styleOptions?.visualMode || 'premium') === opt.val ? 'bg-white dark:bg-bg-3 text-accent shadow-sm' : 'text-fg-3'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                 <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-fg-3 uppercase">Compact View</span>
                    <button
                        onClick={() => updateNodeData({ styleOptions: { ...(nodeData.styleOptions || {}), compactView: !nodeData.styleOptions?.compactView } })}
                        className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
                            nodeData.styleOptions?.compactView ? 'bg-accent' : 'bg-bg-3 dark:bg-bg-3'
                        }`}
                    >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            nodeData.styleOptions?.compactView ? 'translate-x-[18px]' : 'translate-x-0.5'
                        }`} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Component Map ---

const CONTROLS_MAP = {
    'paneSelector': PaneControl,
    'chartTypeSelector': ChartTypeControl,
    'candleColors': CandleThemeControl,
    'opacity': OpacityControl,
    'volumeColors': CandleThemeControl, // Reusing candle theme logic for volume up/down
    'lineColor': LineColorControl,
    'lineWidth': LineThicknessControl,
    'lineStyle': LineStyleControl,
    'lineType': LineTypeControl,
    'areaColors': AreaColorsControl,
    'bandColors': BandColorsControl,
    'markerSettings': MarkerSettingsControl,
    'tradeMarkerSettings': TradeMarkerSettingsControl,
    'levelDisplaySettings': LevelDisplayControl,
    'deeTrendSlope': DeeTrendSlopeControl,
    'indicatorTheme': IndicatorThemeControl
};

// --- Main Component ---

const ChartStylingSection = ({ nodeType, nodeData, updateNodeData, nodeId, allNodes = [], defaultOutputStyles = {} }) => {
    const { entry: displayEntry } = useNodeVisualOverrides(nodeId, undefined);
    const [isOpen, setIsOpen] = useState(true);

    // 1. Get List of Controls from Central Config
    const controls = getStylingControls(nodeType, nodeData);
    
    // 2. Hide component styles ONLY if strictly custom visual AND no outputs to style (or explicitly hidden)
    // Robust check: ensure any slope-equivalent node (dee_trend_slope or specialized overlay) is excluded
    const isSlopeNode = nodeData?.indicator === 'dee_trend_slope' || nodeData?.indicator === 'trend_slope' || nodeType === 'dee_trend_slope';
    const showComponentStyles = nodeData?.outputs?.length > 1 && !isSlopeNode;

    if (!controls || controls.length === 0) return null;

    // Wrapper Wrapper Wrapper
    const updateWrapper = (updates) => updateNodeData(nodeId, updates);
    //* R6-T2 — the same "Overridden" signal as the Live Analysis panel: the record entry holds a
    //  styling member for this node (node-level granularity, one shared predicate).
    const isOverridden = hasStylingOverride(displayEntry);

    return (
        <div className="border border-[var(--divider)] dark:border-white/5 rounded-xl overflow-hidden mt-6 bg-white dark:bg-bg-2">
             {/* Header */}
             <div onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-3 bg-white dark:bg-bg-2 cursor-pointer border-b border-divider/30 hover:bg-bg-3">
                <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-lg bg-accent/10/10 text-accent"><FiDroplet size={16} /></div>
                    <span className="text-[11px] font-bold text-accent uppercase tracking-widest">Chart Styling & Overlay</span>
                    {isOverridden && (
                        <span
                            data-testid={`override-dot-${nodeId}`}
                            title="Overridden"
                            aria-hidden="true"
                            className="w-1.5 h-1.5 rounded-full bg-[var(--color-accent)] shrink-0"
                        />
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Tooltip content={isNodeShownOnChart(nodeType, nodeData) ? "Hide from Chart" : "Show on Chart"} placement="top">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            //! R1-T3 — the ONE toggle helper; `updateNodeData` is the Builder's display chokepoint.
                            updateWrapper(buildNodeVisualTogglePatch(nodeType, nodeData, 'visibility'));
                        }}
                        className={`p-1.5 rounded-md transition-colors ${
                            isNodeShownOnChart(nodeType, nodeData)
                                ? 'text-accent bg-accent/10/20 dark:text-accent'
                                : 'text-fg-3 hover:bg-bg-3 dark:hover:bg-bg-2'
                        }`}
                        aria-label={isNodeShownOnChart(nodeType, nodeData) ? "Hide from Chart" : "Show on Chart"}
                    >
                        {isNodeShownOnChart(nodeType, nodeData) ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                    </button>
                    </Tooltip>
                    <FiChevronUp size={16} className={`text-fg-3 transition-transform ${isOpen ? '' : 'rotate-180'}`} />
                </div>
             </div>

             {/* Content */}
             {isOpen && (
                 <div className="p-4 space-y-6 border-t border-divider/30">
                     {/* Dynamic Controls Loop */}
                     {controls.map(controlId => {
                         const ControlComponent = CONTROLS_MAP[controlId];
                         if (!ControlComponent) return null;
                         return <ControlComponent key={controlId} nodeData={nodeData} updateNodeData={updateWrapper} nodeId={nodeId} nodeType={nodeType} allNodes={allNodes} defaultOutputStyles={defaultOutputStyles} updateNodeDataById={updateNodeData} />;
                     })}

                     {/* Multi-Output Component Styles (Generic Fallback) */}
                     {/* EXCEPTION: DeeTrendSlope has its own custom controls, so hide generic list */}
                     {showComponentStyles && (
                        <div className="pt-4 border-t border-[var(--divider)] dark:border-white/5 space-y-3">
                             <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-1">Component Styles</label>
                             <div className="grid grid-cols-1 gap-3">
                                 {nodeData.outputs.map(key => (
                                     <OutputStylePicker
                                         key={key}
                                         label={key.replace(/_/g, ' ').toUpperCase()}
                                         style={{ lineWidth: 2, ...(defaultOutputStyles[key] || {}), ...(nodeData.outputStyles?.[key] || {}) }}
                                         isHistogram={key.toLowerCase().includes('hist')}
                                         isArea={nodeData.chartType === 'area'}
                                         onChange={(newStyle) => updateWrapper({ outputStyles: { ...(nodeData.outputStyles || {}), [key]: newStyle } })}
                                     />
                                 ))}
                             </div>
                        </div>
                     )}
                 </div>
             )}
        </div>
    );
};

export default ChartStylingSection;
